import React, { useEffect, useRef } from 'react';
import { DialogContent, LinearProgress, Dialog as MuiDialog, styled } from '@mui/material';
import { isUndefined } from 'lodash';
import uuid from 'react-uuid';
import { DIALOG_TITLES_NOT_GRID } from 'common/constants/messages/validations';
import useDataSheetDialogActions from 'common/hooks/useDataSheetDialogActions';
import { DialogProps } from './Dialog.props';
import { dialogClasses, StyledMuiDialog } from './Dialog.styles';
import DialogTitle from './DialogTitle';

const Dialog = (props: DialogProps) => {
  const {
    open,
    title = '',
    children,
    onClose,
    fullWidth = false,
    maxWidth,
    classes,
    dialogProps = {},
    isLoading = false,
    hideTitle = false,
    subtitle,
    PaperProps,
    loadingText,
    customDialogRef,
    loadingIncrement,
    ...additionalProps
  } = props;

  const { disableBackdropClick, disableEscapeKeyDown = false } = dialogProps;
  const dialogRef = useRef<string>(customDialogRef ?? uuid());
  const shouldShowContent = !isUndefined(loadingText) ? !isLoading && !loadingText : true;

  const { setDialogFlag } = useDataSheetDialogActions();

  useEffect(() => {
    const dialogId = dialogRef.current;

    if (setDialogFlag && !DIALOG_TITLES_NOT_GRID.includes(title)) {
      setDialogFlag({
        open,
        dialogId,
        cellId: dialogProps.cellKey,
        disableWhenLedgerOpen: dialogProps.disableWhenLedgerOpen,
      });
    }

    return () => {
      if (setDialogFlag && !DIALOG_TITLES_NOT_GRID.includes(title)) {
        setDialogFlag({ open: false, dialogId, cellId: null });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = (event: React.MouseEvent<HTMLElement>, reason?: string) => {
    if (disableBackdropClick && reason && reason === 'backdropClick') {
      return;
    }

    onClose();
  };

  return (
    <StyledMuiDialog
      fullWidth={fullWidth}
      maxWidth={maxWidth || 'md'}
      onClose={handleClose}
      open={open}
      classes={classes}
      disableEscapeKeyDown={disableEscapeKeyDown}
      PaperProps={PaperProps}
      {...additionalProps}>
      {!hideTitle && (
        <DialogTitle onClose={onClose} dialogProps={dialogProps}>
          {dialogProps.title ?? title}
        </DialogTitle>
      )}
      {subtitle && <DialogContent className={dialogClasses.subtitle}>{subtitle}</DialogContent>}
      {isLoading && (
        <LinearProgress
          color="primary"
          value={loadingIncrement}
          variant={loadingIncrement ? 'determinate' : 'indeterminate'}
        />
      )}
      {isLoading && loadingText && <DialogContent className={dialogClasses.loadingText}>{loadingText}</DialogContent>}
      {shouldShowContent && <DialogContent className={dialogClasses.content}>{children}</DialogContent>}
    </StyledMuiDialog>
  );
};

export default Dialog;
